'use client'

import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useLocation } from 'react-router-dom'
import { AnimatePresence, motion } from 'framer-motion'
import { supabase } from '@/integrations/supabase/client'
import { useAuth } from '@/hooks/useAuth'
import { HelpCircle, X, Send, ThumbsUp, ThumbsDown, UserRound, Loader2, Paperclip, Sparkles, MessageSquare, Minimize2 } from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'

/**
 * Floating "?" help button + single-thread chat, styled in harmony with
 * the Parent Dashboard (Dark Navy / Midnight Canvas, Electric Blue #0EA5E9,
 * Gold accents, and high-contrast typography).
 */

interface Msg {
  id: string
  sender: 'user' | 'ai' | 'admin'
  content: string
  attachment_url?: string | null
  created_at: string
}

const call = async (action: string, extra: Record<string, any> = {}) => {
  const { data, error } = await supabase.functions.invoke('support', { body: { action, ...extra } })
  if (error) throw error
  return data
}

function SupportAttachment({ path }: { path: string }) {
  const [url, setUrl] = useState<string | null>(null)
  useEffect(() => {
    supabase.storage.from('support-attachments').createSignedUrl(path, 60 * 60)
      .then(({ data }) => { if (data?.signedUrl) setUrl(data.signedUrl) })
  }, [path])
  if (!url) return <span style={{ fontSize: 11, color: '#9AA7BC', display: 'block', marginTop: 6 }}>📎 loading attachment…</span>
  return <img src={url} alt="attachment" style={{ maxWidth: '100%', borderRadius: 10, display: 'block', marginTop: 8, border: '1px solid rgba(255,255,255,0.12)' }} />
}

// ── Markdown rendering for AI/admin replies (Kit 18) ──
// Single newlines become markdown hard breaks so replies keep their line structure.
const mdBreaks = (raw: string) => raw.replace(/(?<!\n)\n(?!\n)/g, '  \n')

const mdComponents = {
  p: (props: any) => <p style={{ margin: 0 }}>{props.children}</p>,
  ul: (props: any) => <ul style={{ margin: '6px 0', paddingLeft: 18 }}>{props.children}</ul>,
  ol: (props: any) => <ol style={{ margin: '6px 0', paddingLeft: 18 }}>{props.children}</ol>,
  li: (props: any) => <li style={{ margin: '2px 0' }}>{props.children}</li>,
  strong: (props: any) => <strong style={{ fontWeight: 800 }}>{props.children}</strong>,
  code: (props: any) => <code style={{ background: 'rgba(255,255,255,0.08)', padding: '1px 5px', borderRadius: 4, fontSize: 12.5 }}>{props.children}</code>,
  a: (props: any) => <a href={props.href} target="_blank" rel="noopener noreferrer" style={{ color: '#38BDF8', textDecoration: 'underline', textUnderlineOffset: '2px', fontWeight: 600, wordBreak: 'break-all' }}>{props.children}</a>,
  h1: (props: any) => <p style={{ margin: '8px 0 2px', fontWeight: 800 }}>{props.children}</p>,
  h2: (props: any) => <p style={{ margin: '8px 0 2px', fontWeight: 800 }}>{props.children}</p>,
  h3: (props: any) => <p style={{ margin: '6px 0 2px', fontWeight: 700 }}>{props.children}</p>,
  h4: (props: any) => <p style={{ margin: '6px 0 2px', fontWeight: 700 }}>{props.children}</p>,
  blockquote: (props: any) => <blockquote style={{ margin: '6px 0', paddingLeft: 10, borderLeft: '2px solid #E8C268', opacity: 0.9 }}>{props.children}</blockquote>,
}

export function SupportWidget({
  plan,
  facilityId,
  isSessionPage,
}: {
  plan?: string | null
  facilityId?: string | null
  isSessionPage?: boolean
}) {
  const { user, userProfile } = useAuth()
  const location = useLocation()
  const isSession = isSessionPage ?? (
    location.pathname.includes('/session') ||
    location.pathname.includes('/conversation/') ||
    location.pathname.includes('/category/') ||
    location.pathname.includes('/university/learn') ||
    location.pathname.includes('/church/session')
  )
  const [open, setOpen] = useState(false)
  const [isDismissed, setIsDismissed] = useState(false)
  const [threadId, setThreadId] = useState<string | null>(null)
  const [messages, setMessages] = useState<Msg[]>([])
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [initializing, setInitializing] = useState(false)
  const [pendingImage, setPendingImage] = useState<{ file: File; previewUrl: string } | null>(null)
  const [uploading, setUploading] = useState(false)
  const [feedbackGiven, setFeedbackGiven] = useState<Set<string>>(new Set())
  const [isDocked, setIsDocked] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false
    return localStorage.getItem('sage_support_bubble_docked') === '1'
  })
  const scrollRef = useRef<HTMLDivElement>(null)

  // Apple-style drag & click distinction
  const isDraggingRef = useRef(false)
  const dragStartPosRef = useRef({ x: 0, y: 0 })
  const [constraints, setConstraints] = useState({ left: -320, right: 0, top: -500, bottom: 0 })

  useEffect(() => {
    const updateConstraints = () => {
      if (typeof window === 'undefined') return
      const w = window.innerWidth
      const h = window.innerHeight
      const elemW = isDocked ? 115 : 60
      const elemH = isDocked ? 38 : 60
      const initialBottom = isSession ? 96 : 20
      const initialRight = 16

      setConstraints({
        left: -(w - elemW - initialRight - 12),
        right: 4,
        top: -(h - elemH - initialBottom - 24),
        bottom: initialBottom - 16,
      })
    }
    updateConstraints()
    window.addEventListener('resize', updateConstraints)
    return () => window.removeEventListener('resize', updateConstraints)
  }, [isDocked, isSession])

  const handleDragStart = (_: any, info: any) => {
    isDraggingRef.current = false
    dragStartPosRef.current = { x: info.point.x, y: info.point.y }
  }

  const handleDrag = (_: any, info: any) => {
    const dx = Math.abs(info.point.x - dragStartPosRef.current.x)
    const dy = Math.abs(info.point.y - dragStartPosRef.current.y)
    if (dx > 4 || dy > 4) {
      isDraggingRef.current = true
    }
  }

  const handleDragEnd = () => {
    setTimeout(() => {
      isDraggingRef.current = false
    }, 120)
  }

  const handleBubbleClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (isDraggingRef.current) return
    if (isDocked) {
      undockBubble()
      setOpen(true)
    } else {
      setOpen(v => !v)
    }
  }

  const dockBubble = useCallback((e?: React.MouseEvent) => {
    e?.stopPropagation()
    setIsDocked(true)
    setOpen(false)
    try { localStorage.setItem('sage_support_bubble_docked', '1') } catch {}
  }, [])

  const undockBubble = useCallback(() => {
    setIsDocked(false)
    try { localStorage.removeItem('sage_support_bubble_docked') } catch {}
  }, [])

  useEffect(() => {
    const handler = () => {
      setIsDismissed(false)
      setIsDocked(false)
      setOpen(true)
    }
    window.addEventListener('open-support-widget', handler)
    window.addEventListener('open-support-chat', handler)
    return () => {
      window.removeEventListener('open-support-widget', handler)
      window.removeEventListener('open-support-chat', handler)
    }
  }, [])

  const buildContext = useCallback(() => ({
    url: typeof window !== 'undefined' ? window.location.href : '',
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    email: user?.email,
    plan: plan || undefined,
    facility_id: facilityId || undefined,
    facility_type: userProfile?.facility_type || undefined,
    facility_org_type: (userProfile as any)?.facility_org_type || undefined,
    facility_role: userProfile?.facility_role || undefined,
    is_affiliate: userProfile?.is_affiliate || undefined,
    affiliate_status: userProfile?.affiliate_status || undefined,
    university_access: (userProfile as any)?.university_access || undefined,
  }), [user?.email, plan, facilityId, userProfile?.facility_type, (userProfile as any)?.facility_org_type, userProfile?.facility_role, userProfile?.is_affiliate, userProfile?.affiliate_status, (userProfile as any)?.university_access])

  const initThread = useCallback(async () => {
    if (!user) return
    setInitializing(true)
    try {
      const data = await call('get_or_create_thread', { context: buildContext() })
      setThreadId(data.thread.id)
      setMessages(data.messages || [])
    } catch (e) {
      console.warn('[support] init failed', e)
    } finally {
      setInitializing(false)
    }
  }, [user, buildContext])

  useEffect(() => {
    if (open && !threadId) initThread()
  }, [open, threadId, initThread])

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' })
  }, [messages, loading])

  const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const items = e.clipboardData?.items
    if (!items) return
    for (const item of items) {
      if (item.type.startsWith('image/')) {
        const file = item.getAsFile()
        if (file) {
          setPendingImage({ file, previewUrl: URL.createObjectURL(file) })
        }
        e.preventDefault()
        return
      }
    }
  }

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) setPendingImage({ file, previewUrl: URL.createObjectURL(file) })
    e.target.value = ''
  }

  const uploadPendingImage = async (): Promise<string | null> => {
    if (!pendingImage || !user || !threadId) return null
    setUploading(true)
    try {
      const ext = pendingImage.file.type.split('/')[1] || 'png'
      const path = `${user.id}/${threadId}/${Date.now()}.${ext}`
      const { error } = await supabase.storage.from('support-attachments').upload(path, pendingImage.file, {
        contentType: pendingImage.file.type,
        upsert: false,
      })
      if (error) throw error
      return path
    } catch (e) {
      console.warn('[support] attachment upload failed', e)
      return null
    } finally {
      setUploading(false)
    }
  }

  const send = async (overrideText?: string, escalate = false) => {
    const text = (overrideText ?? input).trim()
    if (!text && !pendingImage) return
    if (!threadId) return

    setLoading(true)
    const optimistic: Msg = {
      id: `optimistic-${Date.now()}`,
      sender: 'user',
      content: text || '(screenshot attached)',
      created_at: new Date().toISOString(),
    }
    setMessages(prev => [...prev, optimistic])
    setInput('')

    try {
      let attachment_url: string | null = null
      if (pendingImage) {
        attachment_url = await uploadPendingImage()
        setPendingImage(null)
      }
      const data = await call('send_message', { thread_id: threadId, content: text, attachment_url, escalate })
      setMessages(prev => [...prev, data.message])
    } catch (e) {
      console.warn('[support] send failed', e)
      setMessages(prev => [...prev, {
        id: `error-${Date.now()}`,
        sender: 'ai',
        content: "Sorry, something went wrong sending that. Please try again in a moment.",
        created_at: new Date().toISOString(),
      }])
    } finally {
      setLoading(false)
    }
  }

  const talkToHuman = async () => {
    if (!threadId) return
    setLoading(true)
    try {
      await call('escalate', { thread_id: threadId })
      const data = await call('get_or_create_thread', { context: buildContext() })
      if (data?.messages) {
        setMessages(data.messages)
      } else {
        setMessages(prev => [...prev, {
          id: `esc-${Date.now()}`,
          sender: 'ai',
          content: "I've flagged this for our support team — a real person will follow up here, usually within a day.",
          created_at: new Date().toISOString(),
        }])
      }
    } finally {
      setLoading(false)
    }
  }

  const giveFeedback = (messageId: string, helpful: boolean) => {
    setFeedbackGiven(prev => new Set(prev).add(messageId))
    if (!helpful) talkToHuman()
  }

  if (!user) return null

  const lastMessage = messages[messages.length - 1]
  const lastIsAi = lastMessage?.sender === 'ai' && !lastMessage.id.startsWith('optimistic')

  return (
    <>
      {/* ── Apple-style Draggable Floating Launcher Bubble (AssistiveTouch pattern) ── */}
      {!isDismissed && (
        <motion.div
          drag
          dragMomentum={false}
          dragElastic={0.08}
          dragConstraints={constraints}
          onDragStart={handleDragStart}
          onDrag={handleDrag}
          onDragEnd={handleDragEnd}
          whileDrag={{ scale: 1.06, cursor: 'grabbing' }}
          style={{
            position: 'fixed',
            right: 16,
            bottom: isSession ? 96 : 20,
            zIndex: 9998,
            cursor: 'grab',
            touchAction: 'none',
            userSelect: 'none',
          }}
        >
          {isDocked ? (
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              whileHover={{ scale: 1.04 }}
              onClick={handleBubbleClick}
              aria-label="Restore Still Waters support chat"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
                padding: '7px 12px 7px 10px',
                background: 'rgba(10, 15, 30, 0.94)',
                backdropFilter: 'blur(12px)',
                border: '1px solid rgba(14, 165, 233, 0.45)',
                borderRadius: 20,
                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.45), 0 0 14px rgba(14, 165, 233, 0.25)',
                color: '#0EA5E9',
                transition: 'box-shadow 0.2s',
              }}
              title="Need help? Drag to move anywhere · Tap to open"
            >
              <div
                style={{
                  width: 20,
                  height: 20,
                  borderRadius: '50%',
                  background: 'rgba(14, 165, 233, 0.22)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <HelpCircle size={13} color="#0EA5E9" />
              </div>
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 800,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: '#F2F5FA',
                  fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
                }}
              >
                Help
              </span>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  setIsDismissed(true)
                }}
                title="Dismiss help button"
                aria-label="Dismiss help button"
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: 'rgba(255,255,255,0.4)',
                  cursor: 'pointer',
                  padding: '2px 0 2px 4px',
                  display: 'flex',
                  alignItems: 'center',
                  lineHeight: 1,
                  transition: 'color 0.15s ease',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.color = '#F2F5FA' }}
                onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.4)' }}
              >
                <X size={11} />
              </button>
            </motion.div>
          ) : (
            <div
              style={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {/* Subtle dock button on corner */}
              {!open && (
                <button
                  type="button"
                  onClick={dockBubble}
                  title="Dock to pill (saves screen space)"
                  aria-label="Dock support bubble to pill"
                  style={{
                    position: 'absolute',
                    top: -5,
                    left: -5,
                    width: 20,
                    height: 20,
                    borderRadius: '50%',
                    background: '#0F1930',
                    border: '1px solid rgba(255, 255, 255, 0.25)',
                    color: '#9AA7BC',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.5)',
                    zIndex: 9999,
                    transition: 'all 0.15s ease',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = '#F2F5FA'
                    e.currentTarget.style.transform = 'scale(1.15)'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = '#9AA7BC'
                    e.currentTarget.style.transform = 'scale(1)'
                  }}
                >
                  <Minimize2 size={10} />
                </button>
              )}

              <button
                type="button"
                onClick={handleBubbleClick}
                aria-label="Open Still Waters support chat"
                title="Need help? Drag to move anywhere · Tap to open"
                style={{
                  width: 56,
                  height: 56,
                  borderRadius: '50%',
                  background: open ? '#131F38' : 'linear-gradient(135deg, #0EA5E9 0%, #0284C7 100%)',
                  color: '#fff',
                  border: '1px solid rgba(255,255,255,0.18)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: open
                    ? '0 12px 30px rgba(0,0,0,0.5)'
                    : '0 10px 28px rgba(14, 165, 233, 0.42), 0 0 15px rgba(14, 165, 233, 0.2)',
                  transition: 'background 0.2s, box-shadow 0.2s',
                }}
              >
                {open ? <X size={22} color="#F2F5FA" /> : <HelpCircle size={26} color="#FFFFFF" />}
              </button>
            </div>
          )}
        </motion.div>
      )}

      {/* Main Support Widget Modal */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 16, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.96 }}
            transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
            style={{
              position: 'fixed',
              bottom: isSession ? 96 : 88,
              right: 16,
              zIndex: 9997,
              width: 384,
              maxWidth: 'calc(100vw - 32px)',
              height: 540,
              maxHeight: isSession ? 'calc(100dvh - 120px)' : 'calc(100dvh - 100px)',
              background: '#0A0F1E', // Canvas token
              borderRadius: 20,
              border: '1px solid rgba(255,255,255,0.12)',
              boxShadow: '0 24px 60px rgba(0, 0, 0, 0.65), 0 0 0 1px rgba(14, 165, 233, 0.2)',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              boxSizing: 'border-box',
            }}
          >
            {/* Header: Midnight Navy with Blue and Gold Accents */}
            <div
              style={{
                flexShrink: 0,
                background: 'linear-gradient(135deg, #0F1930 0%, #172646 100%)',
                padding: '16px 18px',
                borderBottom: '1px solid rgba(255,255,255,0.10)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                color: '#F2F5FA',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div
                  style={{
                    width: 34,
                    height: 34,
                    borderRadius: 10,
                    background: 'rgba(14, 165, 233, 0.15)',
                    border: '1px solid rgba(14, 165, 233, 0.35)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#0EA5E9',
                  }}
                >
                  <Sparkles size={18} />
                </div>
                <div>
                  <div style={{ fontWeight: 800, fontSize: 15, color: '#F2F5FA', letterSpacing: '-0.01em', display: 'flex', alignItems: 'center', gap: 6 }}>
                    Still Waters AI Support
                  </div>
                  <div style={{ fontSize: 11.5, color: '#9AA7BC', marginTop: 1 }}>
                    AI answers instantly · human escalation anytime
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <button
                  onClick={dockBubble}
                  style={{
                    background: 'rgba(255,255,255,0.06)',
                    border: '1px solid rgba(255,255,255,0.10)',
                    borderRadius: 8,
                    padding: '5px 9px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 5,
                    cursor: 'pointer',
                    color: '#9AA7BC',
                    fontSize: 11.5,
                    fontWeight: 700,
                    transition: 'all 0.15s ease',
                  }}
                  title="Dock to screen edge so it won't block mobile content"
                  onMouseEnter={(e) => { e.currentTarget.style.color = '#F2F5FA' }}
                  onMouseLeave={(e) => { e.currentTarget.style.color = '#9AA7BC' }}
                >
                  <Minimize2 size={12} />
                  <span>Dock</span>
                </button>

                <button
                  onClick={() => setOpen(false)}
                  style={{
                    background: 'rgba(255,255,255,0.06)',
                    border: '1px solid rgba(255,255,255,0.10)',
                    borderRadius: 8,
                    width: 28,
                    height: 28,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    color: '#9AA7BC',
                    transition: 'all 0.15s ease',
                  }}
                  title="Close chat"
                >
                  <X size={15} />
                </button>
              </div>
            </div>

            {/* Messages Scroll Area */}
            <div
              ref={scrollRef}
              style={{
                flex: 1,
                minHeight: 0,
                overflowY: 'auto',
                padding: '16px 14px',
                display: 'flex',
                flexDirection: 'column',
                gap: 12,
                background: '#0A0F1E',
                boxSizing: 'border-box',
              }}
            >
              {initializing ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, gap: 10, color: '#9AA7BC', padding: 32 }}>
                  <Loader2 size={24} style={{ animation: 'spin 1s linear infinite', color: '#0EA5E9' }} />
                  <span style={{ fontSize: 12 }}>Connecting to Still Waters Support…</span>
                </div>
              ) : messages.length === 0 ? (
                <div
                  style={{
                    background: 'rgba(15, 25, 48, 0.75)',
                    border: '1px solid rgba(255, 255, 255, 0.08)',
                    borderRadius: 14,
                    padding: '20px 16px',
                    textAlign: 'center',
                    margin: 'auto 0',
                  }}
                >
                  <div
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: 12,
                      background: 'rgba(14, 165, 233, 0.12)',
                      border: '1px solid rgba(14, 165, 233, 0.25)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 12px',
                      color: '#0EA5E9',
                    }}
                  >
                    <MessageSquare size={20} />
                  </div>
                  <div style={{ fontWeight: 700, fontSize: 14, color: '#F2F5FA', marginBottom: 6 }}>
                    How can we help today?
                  </div>
                  <div style={{ fontSize: 12.5, color: '#9AA7BC', lineHeight: 1.5 }}>
                    Ask about curriculum, setting up your children, dashboard navigation, or billing. If you need a person, just ask!
                  </div>
                </div>
              ) : (
                messages.map(m => {
                  const isUser = m.sender === 'user'
                  const isAdmin = m.sender === 'admin'
                  return (
                    <div
                      key={m.id}
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: isUser ? 'flex-end' : 'flex-start',
                        maxWidth: '100%',
                      }}
                    >
                      {isAdmin && (
                        <div
                          style={{
                            fontSize: 10,
                            fontWeight: 800,
                            color: '#E8C268', // Gold accent
                            textTransform: 'uppercase',
                            letterSpacing: '0.04em',
                            marginBottom: 4,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 4,
                          }}
                        >
                          <UserRound size={12} /> Still Waters Support Team
                        </div>
                      )}
                      <div
                        style={{
                          maxWidth: '86%',
                          padding: '11px 14px',
                          borderRadius: isUser ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                          fontSize: 13.5,
                          lineHeight: 1.55,
                          whiteSpace: isUser ? 'pre-wrap' : 'normal',
                          wordBreak: 'break-word',
                          background: isUser
                            ? 'linear-gradient(135deg, #0EA5E9 0%, #0284C7 100%)'
                            : isAdmin
                            ? 'rgba(232, 194, 104, 0.12)'
                            : '#131F38', // Card token
                          border: isUser
                            ? 'none'
                            : isAdmin
                            ? '1px solid rgba(232, 194, 104, 0.35)'
                            : '1px solid rgba(255, 255, 255, 0.10)',
                          color: isUser ? '#FFFFFF' : isAdmin ? '#FDF4DC' : '#F2F5FA',
                          boxShadow: isUser
                            ? '0 4px 14px rgba(14, 165, 233, 0.28)'
                            : '0 4px 12px rgba(0, 0, 0, 0.25)',
                        }}
                      >
                        {isUser ? (
                          m.content.split(/(https?:\/\/[^\s)]+)/g).map((part, i) =>
                            /^https?:\/\//.test(part) ? (
                              <a
                                key={i}
                                href={part}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  color: '#BAE6FD',
                                  textDecoration: 'underline',
                                  textUnderlineOffset: '2px',
                                  fontWeight: 600,
                                  wordBreak: 'break-all',
                                }}
                              >
                                {part}
                              </a>
                            ) : (
                              <React.Fragment key={i}>{part}</React.Fragment>
                            )
                          )
                        ) : (
                          <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
                            {mdBreaks(m.content)}
                          </ReactMarkdown>
                        )}
                        {m.attachment_url && <SupportAttachment path={m.attachment_url} />}
                      </div>
                    </div>
                  )
                })
              )}

              {loading && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    background: '#131F38',
                    border: '1px solid rgba(255,255,255,0.08)',
                    borderRadius: 14,
                    padding: '8px 14px',
                    width: 'fit-content',
                    color: '#9AA7BC',
                    fontSize: 12,
                  }}
                >
                  <Loader2 size={14} style={{ animation: 'spin 1s linear infinite', color: '#0EA5E9' }} />
                  <span>Still Waters AI is thinking…</span>
                </div>
              )}

              {/* Feedback chips under the latest AI reply */}
              {lastIsAi && !feedbackGiven.has(lastMessage.id) && !loading && (
                <div
                  style={{
                    display: 'flex',
                    gap: 6,
                    alignItems: 'center',
                    marginTop: 2,
                    padding: '4px 6px',
                    background: 'rgba(15, 25, 48, 0.6)',
                    borderRadius: 10,
                    width: 'fit-content',
                    border: '1px solid rgba(255,255,255,0.06)',
                  }}
                >
                  <span style={{ fontSize: 11, color: '#9AA7BC' }}>Helpful?</span>
                  <button
                    onClick={() => giveFeedback(lastMessage.id, true)}
                    style={{
                      background: 'rgba(74, 222, 128, 0.12)',
                      border: '1px solid rgba(74, 222, 128, 0.3)',
                      borderRadius: 6,
                      padding: '3px 7px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                    title="Yes, this helped"
                  >
                    <ThumbsUp size={12} color="#4ADE80" />
                  </button>
                  <button
                    onClick={() => giveFeedback(lastMessage.id, false)}
                    style={{
                      background: 'rgba(251, 113, 133, 0.12)',
                      border: '1px solid rgba(251, 113, 133, 0.3)',
                      borderRadius: 6,
                      padding: '3px 7px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                    title="No, request escalation"
                  >
                    <ThumbsDown size={12} color="#FB7185" />
                  </button>
                  <div style={{ width: 1, height: 12, background: 'rgba(255,255,255,0.12)', margin: '0 2px' }} />
                  <button
                    onClick={talkToHuman}
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      color: '#0EA5E9',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      textDecoration: 'underline',
                      textUnderlineOffset: '2px',
                    }}
                  >
                    Talk to a human
                  </button>
                </div>
              )}
            </div>

            {/* Pending Screenshot Preview Banner */}
            {pendingImage && (
              <div
                style={{
                  flexShrink: 0,
                  padding: '8px 14px',
                  background: '#131F38',
                  borderTop: '1px solid rgba(255,255,255,0.10)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                }}
              >
                <img
                  src={pendingImage.previewUrl}
                  alt="attachment preview"
                  style={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 6, border: '1px solid #0EA5E9' }}
                />
                <span style={{ fontSize: 12, color: '#F2F5FA', flex: 1 }}>Screenshot attached</span>
                <button
                  onClick={() => setPendingImage(null)}
                  style={{
                    background: 'rgba(255,255,255,0.08)',
                    border: 'none',
                    borderRadius: 6,
                    padding: 4,
                    cursor: 'pointer',
                    color: '#9AA7BC',
                  }}
                >
                  <X size={14} />
                </button>
              </div>
            )}

            {/* Input Bar — Non-overflowing, solid border-box flex item */}
            <div
              style={{
                flexShrink: 0,
                padding: '10px 12px',
                background: '#0F1930', // Card surface
                borderTop: '1px solid rgba(255,255,255,0.10)',
                display: 'flex',
                gap: 8,
                alignItems: 'flex-end',
                boxSizing: 'border-box',
              }}
            >
              <label
                style={{
                  cursor: 'pointer',
                  color: '#9AA7BC',
                  padding: 8,
                  borderRadius: 8,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  transition: 'color 0.15s ease',
                  flexShrink: 0,
                }}
                title="Attach an image / screenshot"
              >
                <Paperclip size={17} />
                <input type="file" accept="image/*" onChange={handleFileInput} style={{ display: 'none' }} />
              </label>

              <textarea
                value={input}
                onChange={e => setInput(e.target.value)}
                onPaste={handlePaste}
                onKeyDown={e => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault()
                    send()
                  }
                }}
                placeholder="Ask a question or paste screenshot…"
                rows={1}
                disabled={!threadId || initializing}
                style={{
                  flex: 1,
                  resize: 'none',
                  border: '1px solid rgba(255,255,255,0.14)',
                  borderRadius: 12,
                  padding: '9px 12px',
                  fontSize: 13.5,
                  fontFamily: 'inherit',
                  minHeight: 38,
                  maxHeight: 80,
                  background: '#0A0F1E',
                  color: '#F2F5FA',
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
              />

              <button
                onClick={() => send()}
                disabled={loading || uploading || (!input.trim() && !pendingImage) || !threadId}
                aria-label="Send message"
                style={{
                  width: 38,
                  height: 38,
                  borderRadius: '50%',
                  border: 'none',
                  flexShrink: 0,
                  background: 'linear-gradient(135deg, #0EA5E9 0%, #0284C7 100%)',
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: (input.trim() || pendingImage) ? 'pointer' : 'default',
                  opacity: (input.trim() || pendingImage) ? 1 : 0.45,
                  boxShadow: (input.trim() || pendingImage) ? '0 4px 14px rgba(14, 165, 233, 0.4)' : 'none',
                  transition: 'all 0.15s ease',
                }}
              >
                {uploading ? (
                  <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
                ) : (
                  <Send size={15} style={{ marginLeft: 2 }} />
                )}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}